import axios from 'axios';

const useAxiosClient = () => {
  // Honor the build-time API base URL so staging / QA / white-label builds
  // don't silently write to production. Falls back to prod for the default build.
  // Tolerant of VITE_API_BASE_URL with or without a trailing /api so we never
  // double it (request paths here do not include the /api prefix).
  const root = (import.meta.env.VITE_API_BASE_URL || 'https://api.zonstaff.com').replace(/\/+$/, '');
  const baseURL = /\/api$/.test(root) ? root : `${root}/api`;
  
  const axiosClient = axios.create({
    baseURL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  // Add request interceptor for authentication
  axiosClient.interceptors.request.use(
    (config) => {
      // Get auth token from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add universal app identifier
      config.headers['X-App-Type'] = 'universal';
      config.headers['X-App-Domain'] = 'universal.zonstaff.com';

      // A GET carries no body, but the Content-Type above is a client-wide
      // default, so axios sends "application/json" on it anyway. Laravel then
      // takes that header as proof the payload is JSON — isJson() tests the
      // header alone, not the method and not whether a body exists — and
      // getInputSource() hands $request->input() the empty body INSTEAD of the
      // query string. Every ?start_date=…&end_date=… becomes invisible server
      // side, which is why Reports asked for dates it had plainly sent.
      //
      // Dropped only when there is nothing to describe; axios sets the header
      // itself for real JSON bodies, so POST and PUT are untouched.
      if (config.data === undefined || config.data === null) {
        if (typeof config.headers?.delete === 'function') {
          config.headers.delete('Content-Type');
        } else {
          delete config.headers['Content-Type'];
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor for error handling
  axiosClient.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Handle unauthorized access
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isLoggedIn');
        
        // Instead of redirecting, just clear the auth state
        // The app will automatically show the login form when isAuthenticated becomes false
        
        // Dispatch a custom event to notify the app about auth state change
        window.dispatchEvent(new CustomEvent('auth-expired'));
      }
      return Promise.reject(error);
    }
  );

  return axiosClient;
};

export default useAxiosClient;